import { useState, useCallback } from "react";
import { algoliaEventIndex, algoliaNewsIndex, algoliaSpeakersIndex } from "@/helpers/utils/algoliaConfig";
import { debounce } from "lodash";
import { useRouter } from "next/navigation";
import { setSelectedRoute } from "@/redux/features/app-slice";
import { useDispatch } from "react-redux";
import Image from "next/image";
import styles from "./Search.module.css";
import SpeakerProfileModal from "../speaker/profile-modal";
import { Speaker } from "@/models/speaker";


type SearchProps = {
    setIsSearchOpen: (value: boolean) => void;
};

// abstract the suggestion types

interface Suggestion {
    objectID: string;
    path: string;
}

interface EventSuggestion extends Suggestion {
    title: string;
}

interface NewsSuggestion extends Suggestion {
    title: string;
    caption: string;
}

interface SpeakersSuggestion extends Suggestion {
    name: string;
    bio: string;
    designation: string;
    image: string;
    linkedinUrl: string;

}

export default function Search({ setIsSearchOpen }: SearchProps) {
    const [query, setQuery] = useState("");
    const dispatch = useDispatch();
    const router = useRouter();
    const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
    const [showSpeakerDialog, setShowSpeakerDialog] = useState(false);
    const [eventSuggestion, setEventSuggestion] = useState<EventSuggestion[]>([]);
    const [newsSuggestion, setNewsSuggestion] = useState<NewsSuggestion[]>([]);
    const [speakerSuggestion, setSpeakerSuggestion] = useState<SpeakersSuggestion[]>([]);
    // Debounce function wrapper for search
    const debouncedSearch = debounce(async (query: string) => {
        const [eventSearchResults, newsSearchResults, speakerSearchResults] = await Promise.all([
            algoliaEventIndex.search(query),
            algoliaNewsIndex.search(query),
            algoliaSpeakersIndex.search(query)
        ]);

        if (eventSearchResults.hits.length > 0) {
            setEventSuggestion(eventSearchResults.hits as EventSuggestion[]);
        } else {

            setEventSuggestion([]);
        }


        if (newsSearchResults.hits.length > 0) {
            setNewsSuggestion(newsSearchResults.hits as NewsSuggestion[]);
        } else {
            setNewsSuggestion([]);
        }

        if (speakerSearchResults.hits.length > 0) {

            setSpeakerSuggestion(speakerSearchResults.hits as SpeakersSuggestion[]);
        } else {
            setSpeakerSuggestion([]);
        }


    }, 300); // 300 ms debounce time

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setQuery(value);

        if (value.length > 0) {
            debouncedSearch(value);
        } else {
            setEventSuggestion([]);
            setNewsSuggestion([]);
            setSpeakerSuggestion([]);
        }
    };

    const navigateToEventPage = (suggestion: EventSuggestion) => {
        const { path } = suggestion;
        setIsSearchOpen(false);

        const isEvents = path.startsWith("events");

        // get the event id from the path
        const eventId = path.split("/").pop();


        // if the path is an event, navigate to the event page
        if (isEvents && eventId) {
            dispatch(setSelectedRoute('/schedule'));
            router.push(`/schedule?eventId=${eventId}`);
        }
    }

    const navigateToNewsPage = (suggestion: NewsSuggestion) => {
        let { path } = suggestion;
        setIsSearchOpen(false);
        path = "https://www.ctribesociety.com/news/" + path + "/";
        // open the href in a new tab
        window.open(path, "_blank");

    }

    const onClose = () => {
        setShowSpeakerDialog(false);
    }

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center pt-0 sm:pt-20 z-50">

            {
                showSpeakerDialog && selectedSpeaker && (
                    <SpeakerProfileModal
                        speaker={selectedSpeaker}
                        onClose={() => setShowSpeakerDialog(false)}
                        isSchedule={false}
                    />
                )
            }
            <div className="w-full min-h-full sm:min-h-[14rem] max-w-full sm:max-w-2xl p-5 bg-white shadow-xl flex flex-col sm:rounded-3xl" id="searchbar">
                <div className="flex flex-row gap-2">
                    <div className="flex w-full p-2 sm:p-0 items-center sm:border-0 sm:border-b sm:border-gray-200 sm:pb-4 border-2 border-[#333E48] rounded-md">
                        <Image
                            src="/images/search_icon.svg"
                            alt="search"
                            width={20}
                            height={20}
                        />
                        <input
                            autoFocus={true}
                            type="search"
                            value={query}
                            style={{ border: 'none' }}
                            onChange={handleChange}
                            placeholder="Search for programming, speakers, latest reads and more"
                            className={`${styles.searchInput} flex-grow w-full p-2 text-lg border-2 border-gray-300 rounded-lg focus:outline-none`}
                            id="searchinput"
                        />
                    </div>

                    <button
                        className="text-sm block sm:hidden"
                        onClick={() => setIsSearchOpen(false)}
                    >
                        Close
                    </button>
                </div>


                {
                    !query || eventSuggestion.length > 0 || newsSuggestion.length > 0 || speakerSuggestion.length > 0 ? (
                        <div className="mt-4 overflow-y-auto sm:max-h-60">
                            {
                                eventSuggestion.length > 0 && (
                                    <span className="text-sm font-normal" style={{ color: "var(--Neutrals-Gray-3)" }}>Programming</span>
                                )
                            }
                            <ul>
                                {eventSuggestion.map((suggestion) => (
                                    <li key={suggestion.objectID}
                                        style={{ color: 'var(--Neutrals-Secondary-Dark-Neutral---Thunder)' }}
                                        data-testid="search-result"
                                        onClick={() => navigateToEventPage(suggestion)}
                                        className="py-2 pr-4 hover:bg-gray-50 cursor-pointer font-normal">
                                        {suggestion.title}
                                    </li>
                                ))}
                            </ul>


                            {
                                newsSuggestion.length > 0 && (
                                    <span className="text-sm font-normal" style={{ color: "var(--Neutrals-Gray-3)" }}>Latest Reads</span>

                                )
                            }
                            <ul>
                                {newsSuggestion.map((suggestion) => (
                                    <li key={suggestion.objectID}
                                        style={{ color: 'var(--Neutrals-Secondary-Dark-Neutral---Thunder)' }}

                                        onClick={() => navigateToNewsPage(suggestion)}
                                        className="py-2 pr-4 hover:bg-gray-50 cursor-pointe font-normal">
                                        {suggestion.title}
                                    </li>
                                ))}
                            </ul>

                            {
                                speakerSuggestion.length > 0 && (
                                    <span className="text-sm font-normal" style={{ color: "var(--Neutrals-Gray-3)" }}>Speakers</span>
                                )
                            }
                            <ul>
                                {speakerSuggestion.map((suggestion) => (
                                    <li key={suggestion.objectID}

                                        onClick={() => {
                                            setSelectedSpeaker({
                                                id: suggestion.objectID,
                                                name: suggestion.name,
                                                bio: suggestion.bio,
                                                designation: suggestion.designation,
                                                image: suggestion.image,
                                                linkedinUrl: suggestion.linkedinUrl
                                            } as Speaker);
                                            setShowSpeakerDialog(true);

                                        }}
                                        style={{ color: 'var(--Neutrals-Secondary-Dark-Neutral---Thunder)' }}
                                        className="py-2 pr-4 hover:bg-gray-50 cursor-pointer font-medium">
                                        {suggestion.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="mx-auto my-8 flex flex-col items-center">
                            <Image
                                src="/images/search-no-results-icon.svg"
                                alt="search"
                                width={44}
                                height={44}



                            />

                            <p className="mt-2" style={{ color: 'var(--Neutrals-Gray-3)', lineHeight: '18px', fontSize: '16px', fontWeight: '400' }}>No results found</p>
                        </div>
                    )
                }
                <button
                    className="mt-auto w-full text-sm hidden sm:block"
                    onClick={() => setIsSearchOpen(false)}
                >
                    Close
                </button>
            </div>
        </div>

    );
}
